// Generated test tracks; live-use boundaries: docs/DEVELOPMENT.md.

import { crackleWorkaround } from "./platform.js";
import { appPath } from "./api.js";

export const TEST_MODE = new URLSearchParams(location.search).has("test");

const W = 360, H = 640, FPS = 15;
const peerConnections = []; // every RTCPeerConnection the SDK opens (test mode only)
let media = null; // generated tracks, created on the first tap
let label = "Test";
let meeting = null;
let sessionId = null;
let sessionLink = null;
let autoplayBlocked = false;
let micConstraints = null; // audio constraints of the SDK's last microphone request

if (TEST_MODE) install();

function install() {
  // Keep a handle on each peer connection so report() can read its stats.
  const Native = window.RTCPeerConnection;
  function TrackedPeerConnection(...args) {
    const pc = new Native(...args);
    peerConnections.push(pc);
    return pc;
  }
  TrackedPeerConnection.prototype = Native.prototype;
  Object.setPrototypeOf(TrackedPeerConnection, Native);
  window.RTCPeerConnection = TrackedPeerConnection;

  // Hand out generated tracks instead of the camera and microphone.
  const md = navigator.mediaDevices;
  md.getUserMedia = async (constraints = {}) => {
    if (constraints.audio) micConstraints = constraints.audio;
    const m = await ensureMedia();
    const tracks = [];
    if (constraints.audio) tracks.push(m.audioTrack.clone());
    if (constraints.video) tracks.push(m.videoTrack.clone());
    return new MediaStream(tracks);
  };
  const device = (kind, deviceId, deviceLabel) => ({
    kind, deviceId, groupId: "test", label: deviceLabel,
    toJSON() { return { kind, deviceId, groupId: "test", label: deviceLabel }; },
  });
  md.enumerateDevices = async () => [
    device("audioinput", "test-mic", "Test microphone"),
    device("videoinput", "test-cam", "Test camera (front)"),
    device("audiooutput", "default", "Default speaker"),
  ];

  window.mitranTest = { report, silence, crackleWorkaround };
}

function ensureMedia() {
  if (!media) prepareTestMedia();
  return media.ready;
}

/**
 * Creates the generated tracks. Call it synchronously inside the
 * "start call" / "join call" tap: browsers only let audio start from a tap.
 * @param {"listener"|"talker"} [role]  shown in the video; picks the voice
 */
export function prepareTestMedia(role) {
  if (!TEST_MODE) return;
  if (role) label = role === "listener" ? "Listener" : "Talker";
  if (media) return;

  // Video: a canvas redrawn FPS times a second. The block and clock move, so
  // the receiver's decoded-frame count must keep rising.
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext("2d");
  const start = performance.now();
  const draw = () => {
    const t = (performance.now() - start) / 1000;
    g.fillStyle = "#fff";
    g.fillRect(0, 0, W, H);
    g.fillStyle = "#000";
    g.fillRect((Math.sin(t * 2) * 0.5 + 0.5) * (W - 80), H * 0.62, 80, 80);
    g.textAlign = "center";
    g.font = "bold 48px system-ui, sans-serif";
    g.fillText(label.toLowerCase(), W / 2, H * 0.4);
    g.font = "32px system-ui, sans-serif";
    g.fillText(t.toFixed(1), W / 2, H * 0.5);
  };
  draw();
  const videoTrack = canvas.captureStream(FPS).getVideoTracks()[0];
  setInterval(draw, 1000 / FPS);

  // Audio: a recorded sentence on a loop (a tone if the file can't load).
  const ctx = new AudioContext();
  ctx.resume?.();
  const dest = ctx.createMediaStreamDestination();
  media = { videoTrack, audioTrack: dest.stream.getAudioTracks()[0], ctx };
  media.ready = (async () => {
    try {
      const file = label === "Talker" ? "/test/talker.mp3" : "/test/listener.mp3";
      const buffer = await ctx.decodeAudioData(await (await fetch(appPath(file))).arrayBuffer());
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(dest);
      source.start();
    } catch (e) {
      console.warn("test voice unavailable, using a tone", e);
      const osc = ctx.createOscillator();
      osc.frequency.value = 440;
      osc.connect(dest);
      osc.start();
    }
    return media;
  })();
}

/** Lets report() look at the call. Called by client/call.js in test mode. */
export function attachTestMeeting(m) {
  meeting = m;
  m.self.on("autoplayError", () => { autoplayBlocked = true; });
}

/** Records the session (and its live link) this browser is in, for report() and silence(). */
export function noteSession(id, link = null) {
  sessionId = id;
  sessionLink = link;
}

/** Stops this browser's heartbeats for `ms`, as when a phone loses signal. */
function silence(ms) {
  if (!sessionLink) return false;
  sessionLink.pause(ms);
  return true;
}

/** Media statistics for health checks. Compare two reports a few seconds apart. */
async function report() {
  const r = {
    at: Date.now(),
    sessionId,
    audioContext: media?.ctx.state ?? null,
    autoplayBlocked,
    micConstraints,
    sent: { audio: 0, video: 0 },
    received: { audioBytes: 0, audioEnergy: 0, videoBytes: 0, videoFrames: 0, videoWidth: 0 },
  };
  for (const pc of peerConnections) {
    if (pc.connectionState === "closed") continue;
    (await pc.getStats()).forEach((s) => {
      if (s.type === "outbound-rtp" && s.kind in r.sent) r.sent[s.kind] += s.bytesSent ?? 0;
      if (s.type === "inbound-rtp" && s.kind === "audio") {
        r.received.audioBytes += s.bytesReceived ?? 0;
        r.received.audioEnergy += s.totalAudioEnergy ?? 0;
      }
      if (s.type === "inbound-rtp" && s.kind === "video") {
        r.received.videoBytes += s.bytesReceived ?? 0;
        r.received.videoFrames += s.framesDecoded ?? 0;
        r.received.videoWidth = Math.max(r.received.videoWidth, s.frameWidth ?? 0);
      }
    });
  }
  const v = document.getElementById("remote-video");
  r.remoteVideo = { shown: !v.hidden, width: v.videoWidth, height: v.videoHeight, time: v.currentTime };
  const a = document.getElementById("remote-audio");
  r.remoteAudio = {
    playing: !a.paused,
    tracks: a.srcObject ? a.srcObject.getAudioTracks().filter((t) => t.readyState === "live").length : 0,
  };
  if (meeting) {
    r.self = { audio: meeting.self.audioEnabled, video: meeting.self.videoEnabled };
    r.others = meeting.participants.joined.toArray().map((p) => ({
      name: p.name, audio: p.audioEnabled, video: p.videoEnabled, stage: p.stageStatus,
    }));
  }
  return r;
}
